/**
 * Projections financières — types et règles d'affichage (fonctions pures, testées).
 *
 * Source des données : ProjectionCache, calculé chaque semaine par
 * api/app/tasks.py::run_projection_cache_calculation, servi par GET /projections/financial.
 */

export type ReliabilityTier = 'FIABLE' | 'MOYEN' | 'NON_FIABLE';

export interface ProjectionRow {
    symbol: string;
    year_n: number;
    year_prev: number;
    current_price: number | null;
    revenue_n_1: number | null;
    revenue_n_est: number | null;
    revenue_n_est_low: number | null;
    revenue_n_est_high: number | null;
    revenue_method: string | null;
    revenue_n_actual: number | null;
    revenue_actual_method: string | null;
    revenue_est_vs_actual: number | null;
    var_revenue: number | null;
    net_income_n_1: number | null;
    net_income_n_est: number | null;
    net_income_n_est_low: number | null;
    net_income_n_est_high: number | null;
    net_income_method: string | null;
    net_income_growth_capped: boolean | null;
    net_income_n_actual: number | null;
    net_income_actual_method: string | null;
    net_income_est_vs_actual: number | null;
    var_net_income: number | null;
    div_n_1: number | null;
    div_n_est: number | null;
    div_n_actual: number | null;
    div_actual_source: 'PAYE' | 'ANNONCE' | 'AUCUN' | string | null;
    div_est_vs_actual: number | null;
    var_div: number | null;
    yield_est: number | null;
    per_est: number | null;
    payout_ratio: number | null;
    net_margin: number | null;
    reliability_tier: ReliabilityTier | null;
    expected_mape: number | null;
    growth_capped: boolean | null;
    _cache_date: string | null;
}

/**
 * Erreur absolue moyenne du modèle (blend 70 % naïf + 30 % ancré TTM), mesurée par
 * scripts/backtest/test_blend.py sur les exercices 2020-2024 (erreurs > 500 % exclues).
 * Ré-exécuté sur la base locale le 2026-10-22, ligne « 30 % ».
 */
export const ERREUR_BACKTEST = {
    caDepuis9M: 4.1,
    caDepuisS1: 14.3,
    rnDepuis9M: 30.8,
    rnDepuisS1: 46.2,
} as const;

/**
 * Faute de publication intermédiaire de l'exercice, le calcul recopie les comptes N-1
 * (`flat_n_minus_1`) : ce n'est pas une projection et ça ne doit pas en avoir l'air.
 */
export const estNonProjete = (row: ProjectionRow): boolean =>
    !row.revenue_n_actual && !row.net_income_n_actual
    && (row.net_income_method === 'flat_n_minus_1' || row.net_income_method === 'none' || !row.net_income_method);

export const estPublie = (row: ProjectionRow): boolean => !!(row.revenue_n_actual || row.net_income_n_actual);

export const estDivergent = (row: ProjectionRow): boolean => !!(row.growth_capped || row.net_income_growth_capped);

export type DividendeAffiche =
    | { nature: 'AUCUN' }
    | { nature: 'REEL'; montant: number; source: 'PAYE' | 'ANNONCE' }
    | { nature: 'ESTIME'; montant: number }
    | { nature: 'INCONNU' };

/**
 * Un dividende AUCUN est stocké à 0 : le tester en « truthy » le faisait disparaître au profit
 * de l'estimation du modèle. Une décision de non-distribution s'affiche comme telle.
 */
export const dividendeAffiche = (row: ProjectionRow): DividendeAffiche => {
    if (row.div_actual_source === 'AUCUN') return { nature: 'AUCUN' };
    if (row.div_n_actual != null && row.div_n_actual > 0) {
        return { nature: 'REEL', montant: row.div_n_actual, source: row.div_actual_source === 'ANNONCE' ? 'ANNONCE' : 'PAYE' };
    }
    if (row.div_n_est != null && row.div_n_est > 0) return { nature: 'ESTIME', montant: row.div_n_est };
    return { nature: 'INCONNU' };
};

/**
 * Payout estimé au-delà de 100 % du résultat net estimé. Deux causes mesurées le 2026-10-22,
 * aucune n'est un bug de formule : dividende extrapolé par la croissance historique alors que le
 * résultat projeté chute (SPHC 273 %), ou payouts historiques déjà > 100 % du résultat consolidé
 * reconduits (TTLS, ABJC, LNBB, TTLC). Signalé, pas corrigé : l'arbitrage du modèle est ouvert.
 */
export const payoutHorsNorme = (row: ProjectionRow): boolean => (row.payout_ratio ?? 0) > 100;

export const libelleFiabilite = (tier: ReliabilityTier | null): string =>
    tier === 'FIABLE' ? 'Fiable' : tier === 'MOYEN' ? 'Moyen' : tier === 'NON_FIABLE' ? 'Non fiable' : '—';

export interface SyntheseProjections {
    rendementMax: ProjectionRow | null;
    perMin: ProjectionRow | null;
    croissanceMax: ProjectionRow | null;
    payoutMedian: number | null;
    counts: {
        total: number;
        published: number;
        estimated: number;
        nonProjete: number;
        risk: number;
        reliable: number;
    };
}

const mediane = (valeurs: number[]): number | null => {
    if (!valeurs.length) return null;
    const v = [...valeurs].sort((a, b) => a - b);
    const m = Math.floor(v.length / 2);
    return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
};

/**
 * Cartes de synthèse. Exclues : les lignes recopiées de N-1 (leur « PER estimé » est le PER courant,
 * leur croissance vaut 0 par construction) et les lignes divergentes (base locale 2026-10-22 : les
 * trois hausses > 100 % — BOAN +1 111 %, BOABF, SAFC — le sont toutes). BOAN est exact mais mesuré sur
 * une base déprimée : résultat 2025 de 409 M (−92 %, provision exceptionnelle de 4,8 Md), vérifié sur
 * le rapport d'activité S2 2025 du 20/04/2026. Une carte « plus forte hausse » le mettrait en avant
 * sans ce contexte. Payout : médiane bornée à
 * ]0 ; 100] — la moyenne était tirée par les cas hors norme (SPHC 273 %).
 */
export const synthese = (data: ProjectionRow[]): SyntheseProjections => {
    const projetees = data.filter(d => !estNonProjete(d) && !estDivergent(d));
    const max = (rows: ProjectionRow[], cle: (r: ProjectionRow) => number | null) =>
        rows.filter(r => cle(r) != null).sort((a, b) => (cle(b) as number) - (cle(a) as number))[0] ?? null;

    const published = data.filter(estPublie).length;
    const nonProjete = data.filter(estNonProjete).length;
    return {
        rendementMax: max(projetees.filter(r => (r.yield_est ?? 0) > 0), r => r.yield_est),
        perMin: max(projetees.filter(r => (r.per_est ?? 0) > 0), r => -(r.per_est as number)),
        croissanceMax: max(projetees.filter(r => !estPublie(r)), r => r.var_net_income),
        payoutMedian: mediane(projetees.map(r => r.payout_ratio ?? 0).filter(p => p > 0 && p <= 100)),
        counts: {
            total: data.length,
            published,
            estimated: data.length - published - nonProjete,
            nonProjete,
            risk: data.filter(estDivergent).length,
            reliable: data.filter(r => r.reliability_tier === 'FIABLE').length,
        },
    };
};

export type StatusFilter = 'all' | 'published' | 'estimated' | 'non_projete' | 'risk';

export const filtreStatut = (row: ProjectionRow, statut: StatusFilter): boolean => {
    if (statut === 'published') return estPublie(row);
    if (statut === 'estimated') return !estPublie(row) && !estNonProjete(row);
    if (statut === 'non_projete') return estNonProjete(row);
    if (statut === 'risk') return estDivergent(row);
    return true;
};

/** Fourchette « estimation ± erreur sectorielle attendue » ; null pour une valeur publiée. */
export const fourchette = (low: number | null, high: number | null, method: string | null): [number, number] | null => {
    if (low == null || high == null || low === high || (method ?? '').startsWith('actual_')) return null;
    return [Math.min(low, high), Math.max(low, high)];
};
